import React from "react";
import { X, Filter, Check, Star } from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  activePlatform: string;
  onSelectPlatform?: (plat: string) => void;
  filterContentMode: "all" | "text_only" | "emoji_only";
  onChangeContentMode: (mode: "all" | "text_only" | "emoji_only") => void;
  filterSentiment: string;
  onChangeSentiment: (sent: string) => void;
  filterUnansweredOnly: boolean;
  onToggleUnansweredOnly: (val: boolean) => void;
  filterStars: string;
  onChangeStars: (stars: string) => void;
  onClearAll: () => void;
  totalFiltered: number;
}

export function SocialMobileFilterDrawer({
  isOpen,
  onClose,
  activePlatform,
  onSelectPlatform,
  filterContentMode,
  onChangeContentMode,
  filterSentiment,
  onChangeSentiment,
  filterUnansweredOnly,
  onToggleUnansweredOnly,
  filterStars,
  onChangeStars,
  onClearAll,
  totalFiltered,
}: Props) {
  if (!isOpen) return null;

  return (
    <div className="social-mobile-drawer-overlay" onClick={onClose}>
      <div
        className="social-mobile-drawer-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Filtros de comentarios"
      >
        {/* Cabecera */}
        <div className="social-mobile-drawer-header">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Filter size={16} color="var(--blue, #7d96ff)" />
            <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "var(--text)" }}>
              Filtros de comentarios
            </h3>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              type="button"
              onClick={onClearAll}
              style={{
                background: "transparent",
                border: "none",
                fontSize: "12px",
                color: "var(--muted)",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              Limpiar
            </button>
            <button
              type="button"
              className="social-mobile-drawer-close"
              onClick={onClose}
              aria-label="Cerrar filtros"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Cuerpo de filtros scrolleable con touch targets grandes */}
        <div style={{ padding: "0 18px 24px 18px", display: "flex", flexDirection: "column", gap: "18px", overflowY: "auto", maxHeight: "70vh" }}>
          {/* 1. Tipo de contenido */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "var(--text)", marginBottom: "8px" }}>
              Tipo de contenido
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px" }}>
              {[
                { key: "all", label: "Todos" },
                { key: "text_only", label: "💬 Con texto" },
                { key: "emoji_only", label: "😊 Solo emojis" },
              ].map(({ key, label }) => {
                const isSelected = filterContentMode === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onChangeContentMode(key as any)}
                    className={`social-filter-touch-btn ${isSelected ? "is-selected" : ""}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2. Sentimiento / Polaridad */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "var(--text)", marginBottom: "8px" }}>
              Sentimiento
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "6px" }}>
              {[
                { key: "ALL", label: "Todos" },
                { key: "negativa", label: "🚨 Reclamos / Negativos" },
                { key: "positiva", label: "⭐ Elogios / Positivos" },
                { key: "neutra", label: "⚪ Neutros" },
              ].map(({ key, label }) => {
                const isSelected = filterSentiment === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onChangeSentiment(key)}
                    className={`social-filter-touch-btn ${isSelected ? "is-selected" : ""}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3. Estado de respuesta */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "var(--text)", marginBottom: "8px" }}>
              Estado de respuesta
            </div>
            <button
              type="button"
              onClick={() => onToggleUnansweredOnly(!filterUnansweredOnly)}
              className={`social-filter-touch-btn full-width ${filterUnansweredOnly ? "is-selected warning" : ""}`}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <span>⏳ Mostrar únicamente los pendientes de respuesta</span>
              {filterUnansweredOnly && <Check size={16} />}
            </button>
          </div>

          {/* 4. Calificación de estrellas (Google BP) */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "var(--text)", marginBottom: "8px" }}>
              Calificación por estrellas
            </div>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {[
                { key: "ALL", label: "Todas" },
                { key: "5", label: "5 ★" },
                { key: "4", label: "4 ★" },
                { key: "3", label: "3 ★" },
                { key: "2", label: "2 ★" },
                { key: "1", label: "1 ★" },
              ].map(({ key, label }) => {
                const isSelected = filterStars === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onChangeStars(key)}
                    className={`social-filter-touch-btn ${isSelected ? "is-selected" : ""}`}
                    style={{ flex: 1, minWidth: "50px" }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Botón inferior principal de pulgar */}
          <div style={{ paddingTop: "8px" }}>
            <button
              type="button"
              className="button button-primary"
              onClick={onClose}
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "14px",
                fontWeight: 700,
                borderRadius: "10px",
                background: "var(--blue, #7d96ff)",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Check size={16} />
              <span>Ver {totalFiltered} comentarios filtrados</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
